import { apiSlice, gqlApiSlice } from '@fly/store';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';

import {
  type CreateEnvironmentRequest,
  CreateEnvironmentResponse,
  EditEnvironmentRequest,
} from '../shared/types';
import {
  getEnvironmentDeploymentsQuery,
  getEnvironmentsQuery,
} from './environments.queries';
import {
  type GetEnvironmentDeploymentsPageInfo,
  type GetEnvironmentsPageInfo,
  buildEnvironmentDeploymentSearchText,
  transformEnvironmentDeployments,
  transformEnvironments,
} from './environments.transformers';

export type { GetEnvironmentsPageInfo };

/** Unified shape for getEnvironments: list + cursor pageInfo */
export type GetEnvironmentsResult = {
  data: Environment[];
  pageInfo: GetEnvironmentsPageInfo;
};

export type GetEnvironmentsQuery = ResultOf<typeof getEnvironmentsQuery>;
type GetEnvironmentsQueryResult = ResultOf<typeof getEnvironmentsQuery>;
export type Environment =
  GetEnvironmentsQueryResult['flyEnvironments']['searchEnvironments']['edges'][number]['node'];

/** One workflow row under `Environment.deploymentDeltas` (GetFlyEnvironments). */
export type EnvironmentDeploymentDelta = NonNullable<
  Environment['deploymentDeltas']
>[number];

type GetEnvironmentDeploymentsQueryResult = ResultOf<
  typeof getEnvironmentDeploymentsQuery
>;
export type GetEnvironmentDeploymentsQuery =
  GetEnvironmentDeploymentsQueryResult;
type GetEnvironmentDeploymentsQueryVariables = VariablesOf<
  typeof getEnvironmentDeploymentsQuery
>;
export type GetEnvironmentDeploymentsArgs = Omit<
  GetEnvironmentDeploymentsQueryVariables,
  'after'
> & {
  first?: number;
};
export type EnvironmentDeployment =
  GetEnvironmentDeploymentsQueryResult['flyEnvironments']['searchEnvironmentDeployments']['edges'][number]['node'] & {
    searchText: string;
  };
export type GetEnvironmentDeploymentsResult = {
  deployments: EnvironmentDeployment[];
  pageInfo: GetEnvironmentDeploymentsPageInfo;
};

// REST service for mutations
export const restEnvironmentsService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createEnvironment: builder.mutation<
      CreateEnvironmentResponse,
      CreateEnvironmentRequest
    >({
      query: (environment) => ({
        url: '/environments',
        method: 'POST',
        data: environment,
      }),
      onQueryStarted: (_, { queryFulfilled, dispatch }) => {
        queryFulfilled.finally(() => {
          dispatch(gqlApiSlice.util.invalidateTags(['environments']));
        });
      },
    }),
    editEnvironment: builder.mutation<void, EditEnvironmentRequest>({
      query: ({ originalName, ...environment }) => ({
        url: `/environments/${originalName}`,
        method: 'PUT',
        data: environment,
      }),
      onQueryStarted: (_, { queryFulfilled, dispatch }) => {
        queryFulfilled.finally(() => {
          dispatch(gqlApiSlice.util.invalidateTags(['environments']));
        });
      },
    }),
    deleteEnvironment: builder.mutation<void, string>({
      query: (name) => ({
        url: `/environments/${name}`,
        method: 'DELETE',
      }),
      onQueryStarted: (_, { queryFulfilled, dispatch }) => {
        queryFulfilled.finally(() => {
          dispatch(gqlApiSlice.util.invalidateTags(['environments']));
        });
      },
    }),
    updateEnvironmentOrder: builder.mutation<
      void,
      {
        environmentIds: number[];
        queryArgs: GetEnvironmentsArgs;
      }
    >({
      query: ({ environmentIds }) => ({
        url: '/environments/order',
        method: 'PUT',
        data: { environmentIds },
      }),
      onQueryStarted: async (
        { environmentIds, queryArgs },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          gqlEnvironmentsService.util.updateQueryData(
            'getEnvironments',
            queryArgs,
            (draft) => {
              const byId = new Map(
                draft.data.map((env) => [Number(env.id), env])
              );
              draft.data = environmentIds
                .map((id) => byId.get(id))
                .filter(Boolean) as typeof draft.data;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(gqlApiSlice.util.invalidateTags(['environments']));
        } finally {
          // Releases display environment order — reordering shifts which
          // environment column a release appears in, so release views must refresh.
          dispatch(gqlApiSlice.util.invalidateTags(['repository-releases']));
        }
      },
    }),
  }),
});

export type GetEnvironmentsArgs = {
  search?: string;
  names?: string;
  nextPageCursor?: string;
} | void;

export const gqlEnvironmentsService = gqlApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEnvironments: builder.query<GetEnvironmentsResult, GetEnvironmentsArgs>({
      query: (args) => ({
        document: getEnvironmentsQuery,
        variables: {
          first: 50,
          search: args && 'search' in args ? args.search : undefined,
          names:
            args && 'names' in args && args.names
              ? args.names.split(',')
              : undefined,
          after:
            args && 'nextPageCursor' in args ? args.nextPageCursor : undefined,
        },
      }),
      transformResponse: (
        response: ResultOf<typeof getEnvironmentsQuery>
      ): GetEnvironmentsResult => {
        const r = transformEnvironments(response);
        return { data: r.data as Environment[], pageInfo: r.pageInfo };
      },
      providesTags: ['environments'],
    }),
    getEnvironmentDeployments: builder.infiniteQuery<
      GetEnvironmentDeploymentsResult,
      GetEnvironmentDeploymentsArgs,
      string | null
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : null,
        getPreviousPageParam: () => null,
        initialPageParam: null,
      },
      query: ({ queryArg, pageParam }) => ({
        document: getEnvironmentDeploymentsQuery,
        variables: {
          ...queryArg,
          ...(pageParam ? { after: pageParam } : {}),
        },
      }),
      transformResponse: (data: GetEnvironmentDeploymentsQueryResult) => {
        const transformed = transformEnvironmentDeployments(data);
        return {
          deployments: transformed.deployments.map((deployment) => ({
            ...deployment,
            searchText: buildEnvironmentDeploymentSearchText(deployment),
          })) as EnvironmentDeployment[],
          pageInfo: transformed.pageInfo,
        };
      },
      providesTags: ['environments'],
    }),
  }),
});

export const {
  useGetEnvironmentsQuery,
  useGetEnvironmentDeploymentsInfiniteQuery,
} = gqlEnvironmentsService;

export const {
  useCreateEnvironmentMutation,
  useEditEnvironmentMutation,
  useDeleteEnvironmentMutation,
  useUpdateEnvironmentOrderMutation,
} = restEnvironmentsService;
